import { Checklist } from "@/bryntum-lib/models/Checklist";
import { triggerNudgeAlert } from "@/other/utils/otherUtils";
import { MoreMenu } from "@/modern/components/more-menu";
import type { MenuComponentProps } from "@/modern/components/more-menu";
import { useSwivuBryntumContext } from "@/react/hooks/useSwivuBryntumContext";
import { useEntityChecklistContext } from "@/react/components/entityViews/checklist/context/EntityChecklistContext";
import { usePinnedChecklists } from "@/react/hooks/usePinnedChecklists";
import { Archive, Bell, Copy, Download, EyeIcon, Paperclip, PencilIcon, PinIcon, PinOffIcon, Trash, Upload } from "lucide-react";
import { memo } from "react";
import { ChecklistAddItemsModal } from "@/react/components/entityViews/checklist/components/editors/ChecklistAddItemsModal";
import ChecklistDetailLarge from "@/react/components/entityViews/checklist/components/ChecklistDetailLarge";
import { ChecklistDetailModalHeader } from "@/react/components/entityViews/checklist/components/editors/ChecklistDetailModal";

interface ChecklistMoreMenuProps {
  checklist: Checklist;
  onSelect?: () => void;
  handleListView?: () => void;
  toggleEditChecklistName: () => void;
  takeMenuItems?: number[];
  onFinish?: () => void;
}

const ChecklistDetailViewModalContent = (props: MenuComponentProps) => {
  const { checklist } = props as MenuComponentProps & { checklist: Checklist };
  return <ChecklistDetailLarge checklist={checklist} />;
};

const ChecklistEditModalContent = (props: MenuComponentProps) => {
  const { checklist } = props as MenuComponentProps & { checklist: Checklist };
  return <ChecklistAddItemsModal checklist={checklist} />;
};

export const ChecklistMoreMenu = ({ checklist, onSelect, handleListView, takeMenuItems = [], onFinish }: ChecklistMoreMenuProps) => {
  const { project } = useSwivuBryntumContext();
  const {
    deleteChecklist,
    toggleArchiveChecklist,
    importChecklistItems,
    exportChecklistItems,
    duplicateChecklistRecord,
    canEditChecklist,
    copyChecklistLink,
    entityRecord: record
  } = useEntityChecklistContext();
  const { togglePinChecklist, isPinnedChecklist } = usePinnedChecklists();

  const isPinned = isPinnedChecklist(+checklist.id);

  const canEdit = canEditChecklist(checklist);

  return (
    <MoreMenu
      record={record}
      disabled={checklist.isPhantom}
      menuItems={[
        {
          itemVariant: "button" as const,
          menuText: "Pin checklist",
          dropdownMenuItemProps: {
            disabled: !canEdit,
            onClick: () => {
              togglePinChecklist(+checklist.id);
              if (!isPinned && onSelect) {
                onSelect();
              }
            }
          },
          Component: () => (
            <div className="flex items-center gap-3 w-full">
              {isPinned ? <PinOffIcon className="size-4" /> : <PinIcon className="size-4" />}
              {isPinned ? "Unpin checklist" : "Pin checklist"}
            </div>
          )
        },
        // {
        //   itemVariant: "button" as const,
        //   menuText: "Edit checklist name",
        //   dropdownMenuItemProps: {
        //     disabled: !canEdit,
        //     onClick: () => {
        //       toggleEditChecklistName();
        //     }
        //   },
        //   Component: () => (
        //     <div className="flex items-center gap-3 w-full">
        //       <Edit3Icon className="size-4" />
        //       Edit checklist name
        //     </div>
        //   )
        // },
        {
          itemVariant: "modal" as const,
          menuItemModalHeaderVariant: "title" as const,
          variant: "title" as const,
          modalTitle: `Edit ${checklist.title} checklist`,
          modalClassName: "max-w-2xl bg-card/40  md:max-w-2xl",
          classNameContent: "overflow-hidden flex flex-col flex-1",
          Icon: PencilIcon,
          menuText: "Edit checklist",
          dropdownMenuItemProps: {
            disabled: !canEdit
          },
          Component: ChecklistEditModalContent,
          componentProps: {
            checklist
          }
        },
        {
          itemVariant: "modal" as const,
          menuItemModalHeaderVariant: "title" as const,
          variant: "header" as const,
          ModalHeader: () => <ChecklistDetailModalHeader checklist={checklist} />,

          modalTitle: `${checklist.title} checklist`,
          modalClassName: "md:max-w-2xl bg-card/40 overflow-hidden",
          classNameContent: "overflow-scroll flex flex-col flex-1",
          Icon: EyeIcon,
          menuText: "Viewd3d34 checklist",
          dropdownMenuItemProps: {
            // disabled: !canEdit
          },
          Component: ChecklistDetailViewModalContent,
          componentProps: {
            checklist
          }
        },
        {
          itemVariant: "separator" as const
        },
        {
          itemVariant: "button" as const,
          menuText: checklist.archived ? "Unarchive" : "Archive",
          dropdownMenuItemProps: {
            disabled: !canEdit,
            onClick: () => toggleArchiveChecklist(checklist)
          },
          Component: () => (
            <div className="flex items-center gap-3 w-full">
              <Archive className="size-4" />
              {checklist.archived ? "Unarchive" : "Archive"}
            </div>
          )
        },
        {
          itemVariant: "button" as const,
          menuText: "Copy Link",
          dropdownMenuItemProps: {
            onClick: () => copyChecklistLink(checklist)
          },
          Component: () => (
            <div className="flex items-center gap-3 w-full">
              <Copy className="size-4" />
              Copy Link
            </div>
          )
        },
        {
          itemVariant: "button" as const,
          menuText: "Duplicate",
          dropdownMenuItemProps: {
            disabled: !canEdit,
            onClick: () => duplicateChecklistRecord(checklist)
          },
          Component: () => (
            <div className="flex items-center gap-3 w-full">
              <Paperclip className="size-4" />
              Duplicate
            </div>
          )
        },
        {
          itemVariant: "separator" as const
        },
        {
          itemVariant: "button" as const,
          menuText: "Import Checklist Items",
          dropdownMenuItemProps: {
            disabled: !canEdit,
            onClick: () => {
              const input = document.createElement("input");
              input.type = "file";
              input.accept = "text/plain";
              input.onchange = (e) => {
                const target = e.target as HTMLInputElement;
                const file = target.files?.[0];
                if (file) {
                  importChecklistItems(checklist, file);
                }
              };
              input.click();
            }
          },
          Component: () => (
            <div className="flex items-center gap-3 w-full">
              <Upload className="size-4" />
              Import Checklist Items
            </div>
          )
        },
        {
          itemVariant: "button" as const,
          menuText: "Export Checklist Items",
          dropdownMenuItemProps: {
            disabled: !canEdit,
            onClick: () => exportChecklistItems(checklist)
          },
          Component: () => (
            <div className="flex items-center gap-3 w-full">
              <Download className="size-4" />
              Export Checklist Items
            </div>
          )
        },
        {
          itemVariant: "button" as const,
          menuText: "Nudge",
          dropdownMenuItemProps: {
            disabled: !canEdit || checklist.assignedBoardUsers.length === 0,
            onClick: () => triggerNudgeAlert(checklist.id, project)
          },
          Component: () => (
            <div className="flex items-center gap-3 w-full">
              <Bell className="size-4" />
              Nudge
            </div>
          )
        },
        {
          itemVariant: "separator" as const
        },
        {
          itemVariant: "button" as const,
          menuText: "Delete",
          dropdownMenuItemProps: {
            disabled: !canEdit,
            className: "text-destructive focus:text-destructive",
            onClick: async () => {
              const isDeleted = await deleteChecklist(checklist, handleListView);
              if (isDeleted) {
                handleListView?.();
                onFinish?.();
              }
            }
          },
          Component: () => (
            <div className="flex items-center gap-3 w-full">
              <Trash className="size-4" />
              Delete checklist
            </div>
          )
        }
      ].filter((_item, i) => takeMenuItems.length === 0 || takeMenuItems.includes(i))}
    />
  );
};

export const ChecklistMoreMenuMemo = memo(ChecklistMoreMenu, (prevProps, nextProps) => {
  // Only re-render if these specific values change
  // Prevents re-render when checklist object reference changes but the ID stays the same
  return (
    prevProps.checklist.id === nextProps.checklist.id &&
    prevProps.onSelect === nextProps.onSelect &&
    // prevProps.handleListView === nextProps.handleListView &&
    prevProps.onFinish === nextProps.onFinish &&
    JSON.stringify(prevProps.takeMenuItems) === JSON.stringify(nextProps.takeMenuItems)
  );
});
